import { useState, useEffect } from "react";
import { collection, addDoc, onSnapshot, orderBy, query, where, serverTimestamp, setDoc, doc, getDoc } from "firebase/firestore";
import { db } from "./firebase";

// ─── Brand Colors ───────────────────────────────────────────
const B = {
  green: "#6AB309",
  greenDark: "#4d8200",
  greenDim: "rgba(106,179,9,0.12)",
  blue: "#045FA5",
  blueDim: "rgba(4,95,165,0.12)",
  bg: "#080B10",
  surface: "#0E1318",
  card: "#131920",
  border: "#1E2A35",
  text: "#FFFFFF",
  textSoft: "#C8D6E5",
  muted: "#6B7A8D",
  amber: "#F59E0B",
  purple: "#7C3AED",
  red: "#DC2626",
};

const TIERS = [
  { id: "Bronze",   emoji: "🥉", soglia: 0,  label: "Partenza" },
  { id: "Silver",   emoji: "🥈", soglia: 3,  label: "3 acquisiti" },
  { id: "Gold",     emoji: "🥇", soglia: 7,  label: "7 acquisiti" },
  { id: "Platinum", emoji: "💎", soglia: 15, label: "15 acquisiti" },
];

const PIPELINE_STEPS = [
  { key: "segnalato",    label: "Segnalato",    icon: "✓" },
  { key: "contattato",   label: "Contattato",   icon: "📞" },
  { key: "appuntamento", label: "Appuntamento", icon: "📅" },
  { key: "proposta",     label: "Proposta",     icon: "📄" },
  { key: "acquisito",    label: "Acquisito",    icon: "✅" },
  { key: "perso",        label: "Perso",        icon: "✗" },
];

const STEP_COLORS = [
  { bg: "#0c2a3a", border: "#185FA5", color: "#378ADD", line: "#185FA5" },
  { bg: "#0a2a30", border: "#0F6E56", color: "#1D9E75", line: "#0F6E56" },
  { bg: "#0d2e1a", border: "#3a7000", color: "#6AB309", line: "#3a7000" },
  { bg: "#1a2800", border: "#6AB309", color: "#a8e040", line: "#6AB309" },
];

function getTierIndex(livello) { return TIERS.findIndex(t => t.id === livello); }
function getNextTier(livello) { const idx = getTierIndex(livello); return idx < TIERS.length - 1 ? TIERS[idx + 1] : null; }
function getProgressPct(acquisiti, livello) {
  const next = getNextTier(livello);
  if (!next) return 100;
  const curr = TIERS[getTierIndex(livello)];
  return Math.min(100, Math.max(0, Math.round(((acquisiti - curr.soglia) / (next.soglia - curr.soglia)) * 100)));
}

function PipelineBar({ stato }) {
  const activeIdx = PIPELINE_STEPS.findIndex(s => s.key === stato);
  const isPerso = stato === "perso";
  const isAcquisito = stato === "acquisito";

  return (
    <div style={{ display: "flex", alignItems: "flex-start", width: "100%" }}>
      {PIPELINE_STEPS.map((step, i) => {
        const isLast = i === PIPELINE_STEPS.length - 1;
        const isDone = i < activeIdx;
        const isActive = i === activeIdx;

        let dotStyle = { width: 22, height: 22, borderRadius: "50%", border: "1.5px solid #2a3a4a", background: "#0E1318", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, zIndex: 1, color: B.muted };
        let labelColor = B.muted;

        if (isAcquisito && i === 4) {
          dotStyle = { ...dotStyle, background: "#1a2800", border: `1.5px solid ${B.green}`, color: B.green };
          labelColor = B.green;
        } else if (isPerso && i === 5) {
          dotStyle = { ...dotStyle, background: "#2a0f0f", border: "1.5px solid #A32D2D", color: "#f87171" };
          labelColor = "#f87171";
        } else if (isActive) {
          dotStyle = { ...dotStyle, background: B.greenDim, border: `1.5px solid ${B.green}`, color: B.green, boxShadow: `0 0 0 3px ${B.greenDim}` };
          labelColor = B.green;
        } else if (isDone) {
          const c = STEP_COLORS[Math.min(i, STEP_COLORS.length - 1)];
          dotStyle = { ...dotStyle, background: c.bg, border: `1.5px solid ${c.border}`, color: c.color };
        }

        let lineColor = "#1E2A35";
        if (isPerso && i >= activeIdx) lineColor = "#A32D2D";
        else if (isDone || (isAcquisito && i === 3)) lineColor = STEP_COLORS[Math.min(i, STEP_COLORS.length - 1)].line;

        return (
          <div key={step.key} style={{ display: "flex", alignItems: "flex-start", flex: isLast ? "0 0 auto" : 1 }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
              <div style={dotStyle}><span style={{ fontSize: 9 }}>{step.icon}</span></div>
              <span style={{ fontSize: 9, color: labelColor, textAlign: "center", lineHeight: 1.3 }}>{step.label}</span>
            </div>
            {!isLast && (
              <div style={{ flex: 1, display: "flex", alignItems: "flex-start", paddingTop: 11, minWidth: 4 }}>
                <div style={{ width: "100%", height: 1.5, background: lineColor }} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function LeadCard({ lead }) {
  const dataStr = lead.dataCreazione?.toDate ? lead.dataCreazione.toDate().toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" }) : "";
  return (
    <div style={{ background: B.surface, border: `1px solid ${B.border}`, borderRadius: 10, padding: "12px 14px", marginBottom: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: "#FFFFFF" }}>{lead.nome} {lead.cognome}</span>
        <span style={{ fontSize: 11, color: B.muted }}>{dataStr}</span>
      </div>
      <div style={{ fontSize: 13, color: "#C8D6E5", marginBottom: 10 }}>
        {lead.email}{lead.telefono && ` · ${lead.telefono}`}
        {lead.note && <><br /><span style={{ color: B.muted, fontStyle: "italic" }}>{lead.note}</span></>}
      </div>
      <PipelineBar stato={lead.stato} />
      {lead.stato === "acquisito" && lead.percorsoAcquistato && (
        <div style={{ background: B.greenDim, border: `1px solid ${B.green}66`, borderRadius: 8, padding: "10px 12px", marginTop: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
            <div>
              <div style={{ fontSize: 12, color: B.greenMid, fontWeight: 600, marginBottom: 2 }}>✅ {lead.percorsoAcquistato}</div>
              <div style={{ fontSize: 11, color: B.muted }}>{lead.tipoPagamento === "rate" ? `${lead.rate?.length} rate` : "Pagamento unico"}</div>
              {lead.tipoPagamento === "rate" && lead.rate && (
                <div style={{ marginTop: 4 }}>
                  {lead.rate.map((r, i) => (
                    <div key={i} style={{ fontSize: 11, color: B.muted }}>Rata {i+1}: €{r.importo} — scad. {r.scadenza ? new Date(r.scadenza).toLocaleDateString("it-IT") : "—"}</div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 12 }}>
              <div style={{ fontSize: 11, color: B.muted }}>La tua commissione</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: B.green }}>€{lead.commissione?.toFixed(2) || "—"}</div>
              <div style={{ fontSize: 11, color: B.muted }}>{lead.commissionePerc}% su €{lead.importoTotale?.toFixed(2)}</div>
            </div>
          </div>
        </div>
      )}
      {lead.stato === "acquisito" && !lead.percorsoAcquistato && (
        <div style={{ display: "inline-flex", alignItems: "center", gap: 5, background: B.greenDim, border: `1px solid ${B.green}`, color: B.green, fontSize: 12, fontWeight: 600, padding: "3px 10px", borderRadius: 20, marginTop: 8 }}>
          💰 Commissione attiva
        </div>
      )}
      {lead.stato === "perso" && (
        <div style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "#2a0f0f", border: "1px solid #7f1d1d", color: "#f87171", fontSize: 12, fontWeight: 600, padding: "3px 10px", borderRadius: 20, marginTop: 8 }}>
          ✗ Lead non acquisito
        </div>
      )}
    </div>
  );
}

export default function ReferralDashboard({ uid, userData }) {
  const livello = userData?.referralLivello || "Bronze";
  const acquisiti = userData?.referralAcquisiti || 0;
  const nextTier = getNextTier(livello);
  const progressPct = getProgressPct(acquisiti, livello);
  const mancano = nextTier ? nextTier.soglia - acquisiti : 0;
  const firstName = userData?.name?.split(" ")[0] || "";

  const [leads, setLeads] = useState([]);
  const [activeTab, setActiveTab] = useState("leads");
  const [form, setForm] = useState({ nome: "", cognome: "", email: "", telefono: "", note: "" });
  const [formOk, setFormOk] = useState(false);
  const [postLibreria, setPostLibreria] = useState([]);
  const [canalePost, setCanalePost] = useState("tutti");
  const [copiedId, setCopiedId] = useState(null);
  const [mesePDF, setMesePDF] = useState(new Date().getMonth());
  const [annoPDF, setAnnoPDF] = useState(new Date().getFullYear());
  const [guidaOpen, setGuidaOpen] = useState(false);

  const MESI = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];
  const COMM_PERC_S = { Bronze: 10, Silver: 12, Gold: 14, Platinum: 18 };

  async function generaPDFStudente() {
    const { jsPDF } = await import("jspdf");
    const leadsAcquisiti = leads.filter(l => l.stato === "acquisito" && l.percorsoAcquistato);
    const righe = [];
    leadsAcquisiti.forEach(lead => {
      if (lead.tipoPagamento === "rate" && lead.rate) {
        lead.rate.forEach((r, i) => {
          if (!r.scadenza) return;
          const scad = new Date(r.scadenza);
          if (scad.getMonth() === mesePDF && scad.getFullYear() === annoPDF) {
            const importoRata = parseFloat(r.importo) || 0;
            const commRata = Math.round(importoRata * (lead.commissionePerc || 10) / 100 * 100) / 100;
            righe.push({ nome: lead.nome + " " + lead.cognome, percorso: lead.percorsoAcquistato, commRata, commissionePerc: lead.commissionePerc, rata: i + 1, totaleRate: lead.rate.length, scadenza: r.scadenza, saldato: lead.rateSaldate?.[i] || false });
          }
        });
      } else if (lead.tipoPagamento === "unico" && lead.dataAcquisto) {
        const dataAcq = lead.dataAcquisto?.toDate ? lead.dataAcquisto.toDate() : new Date(lead.dataAcquisto);
        if (dataAcq.getMonth() === mesePDF && dataAcq.getFullYear() === annoPDF) {
          righe.push({ nome: lead.nome + " " + lead.cognome, percorso: lead.percorsoAcquistato, commRata: lead.commissione, commissionePerc: lead.commissionePerc, rata: null, scadenza: null, saldato: lead.commissioneSaldata || false });
        }
      }
    });
    if (righe.length === 0) { alert("Nessuna commissione per " + MESI[mesePDF] + " " + annoPDF); return; }
    const doc2 = new jsPDF();
    doc2.setFontSize(16);
    doc2.text("MindSell Academy - Le mie Commissioni", 20, 20);
    doc2.setFontSize(12);
    doc2.text(`${userData?.name || ""} — ${MESI[mesePDF]} ${annoPDF}`, 20, 30);
    doc2.line(20, 35, 190, 35);
    let y = 45;
    let totale = 0;
    righe.forEach((r, i) => {
      doc2.setFontSize(11);
      doc2.setTextColor(40, 40, 40);
      doc2.text(`${i + 1}. ${r.nome} | ${r.percorso}`, 20, y);
      doc2.setFontSize(10);
      doc2.setTextColor(100, 100, 100);
      if (r.rata) doc2.text(`Rata ${r.rata}/${r.totaleRate} | Scadenza: ${r.scadenza ? new Date(r.scadenza).toLocaleDateString("it-IT") : "—"}`, 25, y + 6);
      else doc2.text("Pagamento unico", 25, y + 6);
      doc2.setFontSize(11);
      doc2.setTextColor(40, 40, 40);
      doc2.text(`€${r.commRata?.toFixed(2)} (${r.commissionePerc}%)`, 160, y, { align: "right" });
      doc2.text(r.saldato ? "✓ Saldato" : "In attesa", 190, y, { align: "right" });
      totale += r.commRata || 0;
      y += 18;
    });
    doc2.line(20, y, 190, y);
    y += 8;
    doc2.setFontSize(13);
    doc2.text(`TOTALE: €${totale.toFixed(2)}`, 190, y, { align: "right" });
    doc2.save(`mie-commissioni-${MESI[mesePDF].toLowerCase()}-${annoPDF}.pdf`);
  }

  // Brochure
  const [brochure, setBrochure] = useState([]);
  const [conferme, setConferme] = useState({});
  const [brochureLoaded, setBrochureLoaded] = useState(false);
  const tutteConfermante = brochure.length === 0 ? true : brochure.filter(b => b.obbligatoria).every(b => conferme[b.id]);

  // Leads da Firestore
  useEffect(() => {
    if (!uid || uid.length < 5) return;
    let unsub = null;
    try {
      const q = query(collection(db, "referrals"), where("studenteUid", "==", uid), orderBy("dataCreazione", "desc"));
      unsub = onSnapshot(q,
        (snap) => setLeads(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
        (err) => console.warn("Referral listener error:", err.code)
      );
    } catch(e) { console.warn("Referral query error:", e); }
    return () => { if (unsub) unsub(); };
  }, [uid]);

  // Brochure da Firestore
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "referralBrochure"), snap => {
      setBrochure(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, []);

  // Conferme lettura da Firestore
  useEffect(() => {
    if (!uid) return;
    const load = async () => {
      const snap = await getDoc(doc(db, "studenti", uid, "referralConferme", "letture"));
      if (snap.exists()) setConferme(snap.data());
      setBrochureLoaded(true);
    };
    load();
  }, [uid]);

  const confermaBrochure = async (brochureId) => {
    const nuove = { ...conferme, [brochureId]: new Date().toISOString() };
    setConferme(nuove);
    await setDoc(doc(db, "studenti", uid, "referralConferme", "letture"), nuove, { merge: true });
  };

  async function submitLead() {
    if (!form.nome || !form.cognome || !form.email) return;
    try {
      await addDoc(collection(db, "referrals"), {
        ...form,
        stato: "segnalato",
        dataCreazione: serverTimestamp(),
        studenteUid: uid,
        studenteName: userData?.name || "",
      });
      setForm({ nome: "", cognome: "", email: "", telefono: "", note: "" });
      setFormOk(true);
      setTimeout(() => setFormOk(false), 4000);
    } catch(e) { console.error("Errore salvataggio lead:", e); }
  }

  // Carica post libreria da Firestore
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "referralPost"), snap => {
      setPostLibreria(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, []);

  function copiaPost(p) {
    navigator.clipboard.writeText(p.testo).then(() => {
      setCopiedId(p.id);
      setTimeout(() => setCopiedId(null), 2500);
    });
  }

  // ─── ALERT DINAMICO ──────────────────────────────────────────
  function renderAlert() {
    const tuttiPersi = leads.length > 0 && leads.every(l => l.stato === "perso");
    const platinum = livello === "Platinum";
    const inCorso = leads.filter(l => l.stato !== "perso" && l.stato !== "acquisito").length;

    if (platinum) return { emoji: "💎", color: "#c4b5fd", bg: "#1e1a3a", border: "#534AB7", msg: <><strong>Sei al livello Platinum!</strong> Hai raggiunto il massimo — grazie per il tuo impegno straordinario.</> };
    if (leads.length === 0) return { emoji: "🚀", color: B.green, bg: B.greenDim, border: B.green, msg: <><strong>Inizia ora!</strong> Segnala il tuo primo contatto e guadagna la tua prima commissione.</> };
    if (tuttiPersi) return { emoji: "💪", color: B.amber, bg: "rgba(245,158,11,0.1)", border: B.amber, msg: <><strong>Non mollare!</strong> Ogni no avvicina al prossimo sì. Segnala un nuovo contatto qualificato.</> };
    if (nextTier && mancano > 0) return { emoji: "⚡", color: B.green, bg: B.greenDim, border: B.green, msg: <><strong>Ti mancano {mancano} lead acquisiti per raggiungere il livello {nextTier.id}.</strong>{inCorso > 0 ? ` Hai ${inCorso} lead in corso — continua così!` : " Segnala nuovi contatti per salire di livello."}</> };
    return null;
  }
  const alert = renderAlert();

  const S = {
    page: { background: B.bg, minHeight: "100vh", padding: "20px 24px 40px", color: B.text },
    card: { background: B.card, border: `1px solid ${B.border}`, borderRadius: 12 },
    input: { background: B.surface, border: `1px solid ${B.border}`, borderRadius: 8, padding: "9px 12px", fontSize: 14, color: B.text, fontFamily: "inherit", width: "100%", outline: "none", resize: "none" },
    label: { fontSize: 12, color: B.textSoft, fontWeight: 500, display: "block", marginBottom: 4 },
    secTitle: { fontSize: 11, fontWeight: 700, color: "#A0B4C8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 },
    btnGreen: { background: B.green, color: "#fff", border: "none", borderRadius: 8, padding: "10px 20px", fontSize: 14, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 7, fontFamily: "inherit", letterSpacing: "0.01em" },
    btnGhost: { background: "transparent", color: B.textSoft, border: `1px solid ${B.border}`, borderRadius: 8, padding: "9px 14px", fontSize: 13, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "inherit" },
  };

  return (
    <div style={S.page}>

      {/* HERO */}
      <div style={{ background: B.card, border: `1px solid ${B.border}`, borderRadius: 14, padding: "1.75rem", marginBottom: 16, position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", right: -60, top: -60, width: 240, height: 240, borderRadius: "50%", background: B.blue, opacity: 0.07 }} />
        <div style={{ position: "absolute", left: -40, bottom: -60, width: 180, height: 180, borderRadius: "50%", background: B.green, opacity: 0.08 }} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", position: "relative" }}>
          <div style={{ fontSize: 11, color: B.muted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>↑ Referral Program</div>
          <button onClick={() => setGuidaOpen(true)} style={{ background: B.greenDim, color: B.green, border: `1px solid ${B.green}`, borderRadius: 8, padding: "6px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 6 }}>
            ❓ Come funziona
          </button>
        </div>
        <h1 style={{ fontSize: 28, fontWeight: 700, color: B.text, margin: "0 0 6px", position: "relative" }}>Ciao, {firstName} 👋</h1>
        <p style={{ fontSize: 14, color: "#C8D6E5", lineHeight: 1.6, margin: 0, position: "relative" }}>Ogni contatto che porti vale. Più lead acquisiti, più sali di livello.</p>
      </div>

      {/* STATS */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 16 }}>
        {[
          { color: B.green, border: "rgba(106,179,9,0.3)", label: "Lead segnalati", val: leads.length, icon: "👥" },
          { color: B.blue, border: "rgba(4,95,165,0.3)", label: "Lead acquisiti", val: acquisiti, icon: "🎯" },
          { color: B.amber, border: "rgba(245,158,11,0.3)", label: "Livello attuale", val: livello, icon: "🏆" },
        ].map((s, i) => (
          <div key={i} style={{ background: B.card, border: `1px solid ${s.border}`, borderRadius: 12, padding: "1.1rem 1rem", borderLeft: `3px solid ${s.color}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
              <span style={{ fontSize: 28 }}>{s.icon}</span>
              <span style={{ fontSize: 11, color: s.color, background: `${s.color}22`, padding: "2px 8px", borderRadius: 20, fontWeight: 600 }}>attivo</span>
            </div>
            <div style={{ fontSize: 34, fontWeight: 700, color: s.color, lineHeight: 1 }}>{s.val}</div>
            <div style={{ fontSize: 13, color: "#FFFFFF", marginTop: 6, fontWeight: 500 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* ALERT */}
      {alert && (
        <div style={{ background: alert.bg, border: `1px solid ${alert.border}`, borderRadius: 10, padding: "12px 16px", marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 20, flexShrink: 0 }}>{alert.emoji}</span>
          <p style={{ fontSize: 13, color: alert.color, lineHeight: 1.5, margin: 0 }}>{alert.msg}</p>
        </div>
      )}

      {/* LIVELLO */}
      <div style={{ ...S.card, padding: "1.25rem", marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 13, color: "#FFFFFF", fontWeight: 500 }}>Livello attuale</span>
            <div style={{ background: "rgba(245,158,11,0.15)", color: B.amber, fontSize: 13, fontWeight: 600, padding: "4px 14px", borderRadius: 20, border: "1px solid rgba(245,158,11,0.4)" }}>🏅 {livello}</div>
          </div>
          {nextTier && <span style={{ fontSize: 12, color: "#C8D6E5", fontWeight: 500 }}>Prossimo: {nextTier.id} — {nextTier.soglia} acquisiti</span>}
        </div>
        <div style={{ height: 8, background: "#1E2A35", borderRadius: 4, overflow: "hidden", marginBottom: 6 }}>
          <div style={{ height: "100%", width: `${progressPct}%`, background: B.green, borderRadius: 4, transition: "width .6s ease" }} />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#C8D6E5", fontWeight: 500 }}>
          <span>{acquisiti} acquisiti</span>
          {nextTier && <span>{nextTier.soglia} per {nextTier.id}</span>}
        </div>
      </div>

      {/* TIERS */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8, marginBottom: 20 }}>
        {TIERS.map(t => {
          const active = t.id === livello;
          return (
            <div key={t.id} style={{ background: active ? B.greenDim : B.surface, border: `1px solid ${active ? B.green : B.border}`, borderRadius: 10, padding: "12px 10px", textAlign: "center" }}>
              <div style={{ fontSize: 36, marginBottom: 6, lineHeight: 1 }}>{t.emoji}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#FFFFFF", marginBottom: 3 }}>{t.id}</div>
              <div style={{ fontSize: 12, color: active ? B.green : "#C8D6E5", fontWeight: active ? 600 : 500, marginBottom: 8 }}>{active ? "Livello attuale" : t.label}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: B.green, margin: "4px 0 2px", letterSpacing: "-0.5px" }}>{t.id === "Bronze" ? "10%" : t.id === "Silver" ? "12%" : t.id === "Gold" ? "14%" : "18%"}</div>
              <div style={{ fontSize: 11, color: B.muted, marginBottom: 4 }}>di commissione</div>
            </div>
          );
        })}
      </div>

      {/* TABS */}
      <div style={{ display: "flex", gap: 4, background: B.surface, border: `1px solid ${B.border}`, borderRadius: 10, padding: 4, marginBottom: 16 }}>
        {[{ id: "leads", label: "I miei lead" }, { id: "post", label: "Post da pubblicare" }].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
            flex: 1, padding: "8px 10px", fontSize: 13, fontWeight: 600, textAlign: "center",
            cursor: "pointer", borderRadius: 7, border: activeTab === tab.id ? `1px solid ${B.border}` : "none",
            background: activeTab === tab.id ? B.card : "transparent",
            color: activeTab === tab.id ? B.text : B.muted, fontFamily: "inherit",
          }}>{tab.label}</button>
        ))}
      </div>

      {/* PANEL LEADS */}
      {activeTab === "leads" && (
        <>
          {/* BROCHURE OBBLIGATORIE */}
          {brochureLoaded && brochure.length > 0 && (
            <div style={{ ...S.card, padding: "1.5rem", marginBottom: 14, border: `1px solid ${tutteConfermante ? B.border : B.green}` }}>
              <div style={S.secTitle}>📋 Prima di iniziare — Leggi i materiali MindSell</div>
              <p style={{ fontSize: 13, color: B.textSoft, marginBottom: 14, lineHeight: 1.6 }}>
                Per segnalare contatti qualificati è importante conoscere bene i nostri servizi. Leggi i documenti qui sotto e confermane la lettura prima di procedere.
              </p>
              {brochure.map(b => {
                const confermato = conferme[b.id];
                return (
                  <div key={b.id} style={{ background: B.surface, border: `1px solid ${confermato ? B.green : B.border}`, borderRadius: 10, padding: "12px 14px", marginBottom: 8, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: B.text, marginBottom: 2 }}>📄 {b.titolo}</div>
                      {b.descrizione && <div style={{ fontSize: 12, color: B.muted }}>{b.descrizione}</div>}
                    </div>
                    <div style={{ display: "flex", gap: 8, flexShrink: 0, alignItems: "center" }}>
                      {b.url && (
                        <a href={b.url} target="_blank" rel="noreferrer" style={{ background: B.blueDim, color: "#60a5fa", border: "1px solid rgba(4,95,165,0.4)", borderRadius: 7, padding: "6px 12px", fontSize: 12, fontWeight: 600, textDecoration: "none" }}>
                          📥 Scarica PDF
                        </a>
                      )}
                      {confermato ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 5, color: B.green, fontSize: 12, fontWeight: 600 }}>
                          ✅ Letto
                        </div>
                      ) : (
                        <button onClick={() => confermaBrochure(b.id)} style={{ background: B.greenDim, color: B.green, border: `1px solid ${B.green}`, borderRadius: 7, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                          Ho letto e compreso
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              {!tutteConfermante && (
                <div style={{ background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.3)", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: B.amber, marginTop: 8 }}>
                  ⚠️ Leggi e conferma tutti i documenti per sbloccare il form di segnalazione.
                </div>
              )}
            </div>
          )}

          {/* FORM SEGNALAZIONE */}
          <div style={{ ...S.card, padding: "1.5rem", marginBottom: 14, opacity: tutteConfermante ? 1 : 0.5, pointerEvents: tutteConfermante ? "auto" : "none" }}>
            <div style={S.secTitle}>Segnala un contatto qualificato</div>
            {brochure.length > 0 && !tutteConfermante ? null : (
              <div style={{ background: B.greenDim, border: `1px solid ${B.green}`, borderRadius: 8, padding: "10px 14px", fontSize: 13, color: B.green, marginBottom: 14, lineHeight: 1.6 }}>
                💡 <strong>Importante:</strong> Segnala solo persone che hanno espresso un reale interesse per la formazione alla vendita. Contatti non qualificati rallentano il processo per tutti.
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
              {[{ k: "nome", ph: "Mario" }, { k: "cognome", ph: "Rossi" }, { k: "email", ph: "mario@esempio.it" }, { k: "telefono", ph: "+39 333 000 0000" }].map(f => (
                <div key={f.k}>
                  <label style={S.label}>{f.k.charAt(0).toUpperCase() + f.k.slice(1)}</label>
                  <input style={S.input} placeholder={f.ph} value={form[f.k]} onChange={e => setForm(p => ({ ...p, [f.k]: e.target.value }))} />
                </div>
              ))}
              <div style={{ gridColumn: "1/-1" }}>
                <label style={S.label}>Note — come hai verificato l'interesse?</label>
                <textarea style={{ ...S.input, height: 70 }} placeholder="Es. mi ha chiesto info sulla formazione per il suo team di 5 venditori..." value={form.note} onChange={e => setForm(p => ({ ...p, note: e.target.value }))} />
              </div>
            </div>
            <button style={S.btnGreen} onClick={submitLead}>
              ➤ Invia segnalazione
            </button>
            {formOk && (
              <div style={{ background: B.greenDim, border: `1px solid ${B.green}`, color: B.green, borderRadius: 8, padding: "10px 14px", fontSize: 13, marginTop: 12 }}>
                ✅ Segnalazione inviata! Ti aggiorneremo sullo stato del contatto.
              </div>
            )}
          </div>

          {/* LISTA LEADS */}
          <div style={{ ...S.card, padding: "1.5rem" }}>
            <div style={S.secTitle}>Stato avanzamento</div>
            {leads.length === 0 ? (
              <p style={{ fontSize: 14, color: B.muted, textAlign: "center", padding: "24px 0" }}>Nessun lead ancora. Inizia a segnalare!</p>
            ) : (
              leads.map(lead => <LeadCard key={lead.id} lead={lead} />)
            )}
          </div>
        </>
      )}

      {/* PANEL PDF COMMISSIONI */}
      {activeTab === "leads" && leads.some(l => l.stato === "acquisito" && l.percorsoAcquistato) && (
        <div style={{ ...S.card, padding: "1.25rem", marginTop: 12 }}>
          <div style={S.secTitle}>📄 Scarica riepilogo commissioni</div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <select value={mesePDF} onChange={e => setMesePDF(Number(e.target.value))} style={{ ...S.input, width: "auto", padding: "6px 10px", fontSize: 13 }}>
              {MESI.map((m, i) => <option key={i} value={i}>{m}</option>)}
            </select>
            <select value={annoPDF} onChange={e => setAnnoPDF(Number(e.target.value))} style={{ ...S.input, width: "auto", padding: "6px 10px", fontSize: 13 }}>
              {[2025, 2026, 2027].map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <button style={S.btnGreen} onClick={generaPDFStudente}>📄 Scarica PDF</button>
          </div>
        </div>
      )}

      {/* PANEL POST */}
      {activeTab === "post" && (
        <div style={{ ...S.card, padding: "1.5rem" }}>
          <div style={S.secTitle}>Post da pubblicare</div>
          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            {[{ id: "tutti", label: "Tutti" }, { id: "linkedin", label: "LinkedIn" }, { id: "instagram", label: "Instagram / Facebook" }].map(ch => (
              <button key={ch.id} onClick={() => setCanalePost(ch.id)} style={{
                padding: "6px 14px", fontSize: 13, borderRadius: 20, cursor: "pointer", fontFamily: "inherit",
                border: `1px solid ${canalePost === ch.id ? B.green : B.border}`,
                background: canalePost === ch.id ? B.greenDim : B.surface,
                color: canalePost === ch.id ? B.green : B.textSoft, fontWeight: canalePost === ch.id ? 600 : 400,
              }}>{ch.label}</button>
            ))}
          </div>
          {postLibreria.filter(p => canalePost === "tutti" || p.canale === canalePost).length === 0 && (
            <p style={{ fontSize: 14, color: B.muted, textAlign: "center", padding: "24px 0" }}>Nessun post disponibile. Il team MindSell aggiungerà presto nuovi contenuti!</p>
          )}
          {postLibreria.filter(p => canalePost === "tutti" || p.canale === canalePost).map(p => (
            <div key={p.id} style={{ background: B.surface, border: `1px solid ${B.border}`, borderRadius: 10, padding: "14px 16px", marginBottom: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                <div>
                  <span style={{ fontSize: 13, fontWeight: 600, color: B.text }}>{p.titolo}</span>
                  <span style={{ fontSize: 11, color: p.canale === "linkedin" ? B.blue : B.purple, background: p.canale === "linkedin" ? B.blueDim : "rgba(124,58,237,0.1)", padding: "2px 8px", borderRadius: 20, marginLeft: 8, fontWeight: 500 }}>
                    {p.canale === "linkedin" ? "LinkedIn" : "Instagram / FB"}
                  </span>
                </div>
                <button onClick={() => copiaPost(p)} style={{ ...S.btnGhost, fontSize: 12, padding: "5px 12px", flexShrink: 0 }}>
                  {copiedId === p.id ? "✓ Copiato!" : "📋 Copia"}
                </button>
              </div>
              <div style={{ fontSize: 13, color: B.textSoft, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{p.testo}</div>
            </div>
          ))}
        </div>
      )}

      {/* GUIDA PROGRAMMA REFERRAL */}
      {guidaOpen && (
        <div onClick={() => setGuidaOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 1000 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: B.card, border: `1px solid ${B.border}`, borderRadius: 16, width: "100%", maxWidth: 640, maxHeight: "80vh", overflowY: "auto", padding: "2rem", position: "relative" }}>
            <button onClick={() => setGuidaOpen(false)} style={{ position: "absolute", top: 16, right: 16, background: "none", border: "none", color: B.muted, fontSize: 22, cursor: "pointer", lineHeight: 1, fontFamily: "inherit" }}>×</button>

            <h2 style={{ fontSize: 20, fontWeight: 700, color: B.text, margin: "0 0 16px" }}>Benvenuto nel Programma Referral MindSell 🤝</h2>
            <p style={{ fontSize: 14, color: B.text, lineHeight: 1.7, margin: "0 0 24px" }}>Hai vissuto il metodo MindSell dall'interno. Ora puoi trasformare quella esperienza in un'opportunità concreta — segnala i tuoi contatti e guadagna una commissione su ogni cliente acquisito.</p>

            <div style={{ fontSize: 13, fontWeight: 700, color: B.green, textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 8px" }}>📌 Come funziona</div>
            <p style={{ fontSize: 14, color: B.text, lineHeight: 1.7, margin: "0 0 24px" }}>Il programma è semplice: segnali un contatto interessato alla formazione alla vendita, noi lo seguiamo, e se diventa cliente ricevi una commissione sul valore del percorso acquistato. Non devi vendere nulla. Il tuo compito è mettere in contatto le persone giuste con noi.</p>

            <div style={{ fontSize: 13, fontWeight: 700, color: B.green, textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 8px" }}>🏆 I livelli e le commissioni</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, margin: "0 0 10px" }}>
              <p style={{ fontSize: 14, color: B.textSoft, margin: 0 }}>🥉 <strong style={{ color: B.text }}>Bronze</strong> — 10% di commissione (livello di partenza)</p>
              <p style={{ fontSize: 14, color: B.textSoft, margin: 0 }}>🥈 <strong style={{ color: B.text }}>Silver</strong> — 12% (da 3 clienti acquisiti)</p>
              <p style={{ fontSize: 14, color: B.textSoft, margin: 0 }}>🥇 <strong style={{ color: B.text }}>Gold</strong> — 14% (da 7 clienti acquisiti)</p>
              <p style={{ fontSize: 14, color: B.textSoft, margin: 0 }}>💎 <strong style={{ color: B.text }}>Platinum</strong> — 18% (da 15 clienti acquisiti)</p>
            </div>
            <p style={{ fontSize: 14, color: B.text, lineHeight: 1.7, margin: "0 0 24px" }}>Il livello sale automaticamente man mano che i tuoi contatti diventano clienti.</p>

            <div style={{ fontSize: 13, fontWeight: 700, color: B.green, textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 8px" }}>🎯 Chi puoi segnalare</div>
            <p style={{ fontSize: 14, color: B.text, lineHeight: 1.7, margin: "0 0 8px" }}>Segnala solo persone che hanno espresso un reale interesse per la formazione alla vendita. I profili più adatti sono:</p>
            <ul style={{ margin: "0 0 12px", paddingLeft: 20 }}>
              <li style={{ fontSize: 14, color: B.textSoft, lineHeight: 1.7 }}>Liberi professionisti che vogliono migliorare le proprie vendite</li>
              <li style={{ fontSize: 14, color: B.textSoft, lineHeight: 1.7 }}>Titolari di piccole imprese con un team commerciale</li>
              <li style={{ fontSize: 14, color: B.textSoft, lineHeight: 1.7 }}>Venditori e commerciali che vogliono strutturare meglio il loro approccio</li>
            </ul>
            <div style={{ background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.3)", borderRadius: 10, padding: "10px 14px", marginBottom: 24, display: "flex", alignItems: "flex-start", gap: 8 }}>
              <span style={{ fontSize: 16, flexShrink: 0 }}>⚠️</span>
              <p style={{ fontSize: 13, color: B.amber, lineHeight: 1.6, margin: 0 }}>Segnala solo contatti qualificati — un contatto non interessato rallenta il processo per tutti e non ti avvicina al prossimo livello.</p>
            </div>

            <div style={{ fontSize: 13, fontWeight: 700, color: B.green, textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 8px" }}>📋 Come segnalare un contatto</div>
            <ol style={{ margin: "0 0 8px", paddingLeft: 20 }}>
              <li style={{ fontSize: 14, color: B.text, lineHeight: 1.7 }}>Vai nella tab "I miei lead"</li>
              <li style={{ fontSize: 14, color: B.text, lineHeight: 1.7 }}>Compila il form con nome, cognome, email e telefono del contatto</li>
              <li style={{ fontSize: 14, color: B.text, lineHeight: 1.7 }}>Aggiungi una nota su come hai verificato il suo interesse</li>
              <li style={{ fontSize: 14, color: B.text, lineHeight: 1.7 }}>Clicca "Invia segnalazione"</li>
            </ol>
            <p style={{ fontSize: 14, color: B.text, lineHeight: 1.7, margin: "0 0 24px" }}>Il nostro team prenderà in carico il contatto entro 24/48 ore.</p>

            <div style={{ fontSize: 13, fontWeight: 700, color: B.green, textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 8px" }}>🔔 Seguire l'avanzamento</div>
            <p style={{ fontSize: 14, color: B.text, lineHeight: 1.7, margin: "0 0 8px" }}>Nella sezione "Stato avanzamento" puoi seguire in tempo reale cosa succede con ogni contatto. Riceverai una notifica nella campanella 🔔 ad ogni aggiornamento.</p>
            <p style={{ fontSize: 14, color: B.text, lineHeight: 1.7, margin: "0 0 8px" }}>Gli stati possibili sono:</p>
            <ul style={{ margin: "0 0 24px", paddingLeft: 20 }}>
              <li style={{ fontSize: 14, color: B.textSoft, lineHeight: 1.7 }}>Segnalato — abbiamo ricevuto il contatto</li>
              <li style={{ fontSize: 14, color: B.textSoft, lineHeight: 1.7 }}>Contattato — il nostro team ha preso contatto</li>
              <li style={{ fontSize: 14, color: B.textSoft, lineHeight: 1.7 }}>Appuntamento — è fissato un incontro</li>
              <li style={{ fontSize: 14, color: B.textSoft, lineHeight: 1.7 }}>Proposta — è stata inviata una proposta commerciale</li>
              <li style={{ fontSize: 14, color: B.textSoft, lineHeight: 1.7 }}>Acquisito — è diventato cliente ✅</li>
              <li style={{ fontSize: 14, color: B.textSoft, lineHeight: 1.7 }}>Perso — la trattativa non è andata a buon fine</li>
            </ul>

            <div style={{ fontSize: 13, fontWeight: 700, color: B.green, textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 8px" }}>💰 Quando ricevi la commissione</div>
            <ul style={{ margin: "0 0 8px", paddingLeft: 20 }}>
              <li style={{ fontSize: 14, color: B.textSoft, lineHeight: 1.7 }}>Pagamento unico — la commissione viene liquidata nel mese in cui il cliente ha pagato</li>
              <li style={{ fontSize: 14, color: B.textSoft, lineHeight: 1.7 }}>Pagamento a rate — ricevi la commissione su ogni rata, nel mese della sua scadenza</li>
            </ul>
            <p style={{ fontSize: 14, color: B.text, lineHeight: 1.7, margin: "0 0 24px" }}>Riceverai un riepilogo mensile scaricabile in PDF dalla sezione "I miei lead".</p>

            <div style={{ fontSize: 13, fontWeight: 700, color: B.green, textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 8px" }}>📣 I post da pubblicare</div>
            <p style={{ fontSize: 14, color: B.text, lineHeight: 1.7, margin: 0 }}>Nella tab "Post da pubblicare" trovi contenuti pronti da condividere sui tuoi profili social. Sono scritti per sembrare autentici — usali così come sono per ottenere il miglior risultato.</p>
          </div>
        </div>
      )}

    </div>
  );
}